package co.maytes.standupbot.dashboard.pages.app.onboarding

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.features.onboarding.ConnectionCard
import co.maytes.standupbot.dashboard.features.onboarding.ConnectionState
import co.maytes.standupbot.dashboard.features.onboarding.FlowNode
import co.maytes.standupbot.dashboard.features.onboarding.InlineError
import co.maytes.standupbot.dashboard.features.onboarding.IntegrationsStore
import co.maytes.standupbot.dashboard.features.onboarding.NoticeTone
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingShell
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingStep
import co.maytes.standupbot.dashboard.features.onboarding.TickRow
import co.maytes.standupbot.dashboard.features.onboarding.WizardBrand
import co.maytes.standupbot.dashboard.features.onboarding.WizardFlow
import co.maytes.standupbot.dashboard.features.onboarding.WizardList
import co.maytes.standupbot.dashboard.features.onboarding.WizardNotice
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/app/onboarding/slack")
@Composable
fun OnboardingSlackPage() {
    val api = rememberApiClient()
    val auth = rememberAuthStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val installError = ctx.route.params["error"]
    val integrations = remember { IntegrationsStore(api) }
    var refreshed by remember { mutableStateOf(false) }
    val workspaceId = auth.currentUser?.workspace?.id

    LaunchedEffect(workspaceId) {
        val wsId = workspaceId ?: return@LaunchedEffect
        integrations.refresh(wsId)
        refreshed = true
    }

    val connected = auth.currentUser?.workspace?.slackConnected == true ||
        integrations.isConnected(IntegrationProviderDto.SLACK)
    val connectionState = when {
        connected -> ConnectionState.Connected
        !refreshed && workspaceId != null -> ConnectionState.Checking
        else -> ConnectionState.Disconnected
    }

    OnboardingShell(
        step = OnboardingStep.Slack,
        headline = if (connected) "Slack is connected" else "Connect Slack",
        subline = if (connected) {
            "Next, link Linear so we can read your team's activity."
        } else {
            "Install StandupBot in your workspace. You'll return here when it's done."
        },
        art = {
            WizardFlow(
                nodes = listOf(
                    FlowNode(WizardBrand.StandupBot, live = connected),
                    FlowNode(WizardBrand.Slack, live = connected),
                ),
                flowing = !connected,
            )
        },
        nextLabel = if (connected) "Continue" else "Install on Slack",
        onNext = if (connected) {
            { ctx.router.navigateTo(OnboardingStep.Linear.path) }
        } else {
            {
                window.location.href =
                    "/oauth/slack/install?returnTo=" + encodeUriComponent("/app/onboarding/slack")
            }
        },
    ) {
        Div(attrs = { classes("sb-wiz-stack") }) {
            if (installError != null) {
                WizardNotice(
                    tone = NoticeTone.Danger,
                    title = when (installError) {
                        "slack-denied" -> "Install cancelled"
                        else -> "Slack install failed"
                    },
                    message = when (installError) {
                        "slack-denied" -> "You cancelled the Slack install. Try again to connect."
                        else -> "Try again to connect."
                    },
                )
            }
            ConnectionCard(
                brand = WizardBrand.Slack,
                title = "Slack workspace",
                detail = if (connected) "StandupBot is installed." else "Posts your standups to a channel.",
                state = connectionState,
            )
            val loadError = integrations.lastError
            if (refreshed && loadError != null) {
                InlineError(
                    title = "Couldn't load integration status",
                    message = loadError,
                    onRetry = {
                        val wsId = workspaceId ?: return@InlineError
                        scope.launch {
                            refreshed = false
                            integrations.refresh(wsId)
                            refreshed = true
                        }
                    },
                )
            }
        }
        Div(attrs = { classes("sb-wiz-stack") }) {
            Span(attrs = { classes("sb-wiz-label") }) { Text("What you get") }
            WizardList {
                TickRow("Standups posted to the channels you pick")
                TickRow("Channel members listed so you can pick the roster")
                TickRow("A quick return to this setup when you're done")
            }
        }
    }
}

private fun encodeUriComponent(value: String): String =
    js("encodeURIComponent(value)").unsafeCast<String>()
